import { useApiState } from 'hooks/useApiState';
import { useAppState } from 'hooks/useAppState';
import { useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useNavigate, useParams } from 'react-router-dom';
import { useAtom } from 'jotai';
import { AcctState } from 'stores/atoms/acctState';
import type { InvitationRefResponseType } from 'types/api/invitationRefResponseType';
import { submit } from 'utils/app';

/* ------------------------------------------------------------------ */
/* 型定義                                                              */
/* ------------------------------------------------------------------ */

export interface UseInvitationIndexResult {
  initialized: boolean;
  processing: boolean;
  invitationState: InvitationRefResponseType | null;
  onAccept: () => Promise<void>;
}

/* ------------------------------------------------------------------ */
/* hook                                                                */
/* ------------------------------------------------------------------ */

/**
 * 招待受付画面のカスタムフック。
 * state / 副作用 / handler を集約し、JSX からロジックを分離する。
 */
export function useInvitationIndex(): UseInvitationIndexResult {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();
  const navigate = useNavigate();
  const { invitationCd } = useParams();

  const [acctState] = useAtom(AcctState);

  const { initializeAcctState, signUpRequest, signOutRequest } = useAppState();
  const { postInvitationRef, postInvitationAccept } = useApiState();

  const [initialized, setInitialized] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [invitationState, setInvitationState] = useState<InvitationRefResponseType | null>(null);

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    (async () => {
      try {
        // 招待情報の取得
        const response = await postInvitationRef({
          invitationCd: invitationCd,
        });
        setInvitationState(response);

        // 招待されたメールアドレスとログイン中のメールアドレスが異なる場合はログアウトする
        if (acctState.user) {
          if (response.member.invitationMailAddress !== acctState.user.mailAddress) {
            await signOutRequest();
            await initializeAcctState(response.company.cd);
          }
        }
      } catch (e) {
        showBoundary(e);
      } finally {
        setInitialized(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- マウント時 1 回のみ実行する初期化処理
  }, []);

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 招待承認ボタン押下時の処理
   */
  const onAccept = () =>
    submit(processing, setProcessing, async () => {
      try {
        // ログイン済の場合は、招待受付APIを呼び出しホーム画面に遷移
        if (acctState.user) {
          await postInvitationAccept({
            invitationCd: invitationCd,
            user: null,
          });
          await initializeAcctState(invitationState.company.cd);
          navigate('/');
        }
        // 未ログインの場合は、認証コードを送信
        else {
          await signUpRequest(invitationState.member.invitationMailAddress);
          navigate('/signin/auth', {
            state: {
              mailAddress: invitationState.member.invitationMailAddress,
              nextTo: `/invitation/${invitationCd}/user`,
            },
          });
        }
      } catch (e) {
        showBoundary(e);
      }
    });

  /* ----------------------------------------------------------------------- */
  /* 戻り値 */
  /* ----------------------------------------------------------------------- */
  return {
    initialized,
    processing,
    invitationState,
    onAccept,
  };
}
